/* eslint-disable no-param-reassign */
/* eslint-disable react-hooks/exhaustive-deps */
import _ from 'lodash'
import { useCallback } from 'react'
import { useStaticQuery, graphql } from 'gatsby'
import camelCaseKeys from 'camelcase-keys'
import moment from '../../helpers/moment'
import {
  useSystemSettings,
  useStores,
} from '../../vendor/react-omnitech-api'
import {
  useCommerceChannel,
} from '../use-commerce-channel'
import { useOrderMethod } from '../use-order-method'

/**
 * Finds and returns an array of time slot codes that match the current time.
 *
 * @param {Array} orderingPeriods - An array of objects,
 *    with properties: 'slotCode', 'fromTime', and 'toTime'.
 * @returns {Array} - An array of time slot codes that match the current time.
 */

const findMatchedTimeSlotCodes = (orderingPeriods = []) => {
  const matchedTimeSlotCodes = _.reduce(
    orderingPeriods,
    (result, orderingPeriod) => {
      const { slotCode, fromTime, toTime } = camelCaseKeys(orderingPeriod, { deep: true })
      const timeFormat = 'LT'
      const from = moment(fromTime, timeFormat)
      const to = moment(toTime, timeFormat)
      if (moment().isBetween(from, to)) {
        result.push(slotCode)
      }
      return _.uniq(result)
    },
    [],
  )

  return matchedTimeSlotCodes
}

function useOrderingPeriod() {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            commerceChannelPublicConfigSchemaVersion
          }
        }
      }
    `,
  )
  const commerceChannelPublicConfigSchemaVersion = _.get(site, 'siteMetadata.commerceChannelPublicConfigSchemaVersion')
  const { commerceChannels } = useCommerceChannel()
  const { orderMethod, store } = useOrderMethod()
  const { getSystemSetting } = useSystemSettings()
  const { stores, fetchStoreDateTimeInformation } = useStores()

  const getCurrentSlotCodes = useCallback(({ submenuOrderPeriods } = {}) => {
    // submenuOrderPeriods provided
    if (_.isPlainObject(submenuOrderPeriods)) {
      const orderingPeriods = _.map(
        submenuOrderPeriods,
        (time = {}, slotCode) => ({ slotCode, ...camelCaseKeys(time) }),
      )
      const matchedTimeSlotCodes = findMatchedTimeSlotCodes(orderingPeriods)
      // To prevent empty array that makes api call result list all items without filters
      const submenuOrderPeriodsFallback = getSystemSetting('store.submenu_order_periods.fallback', [])
      if (_.isEmpty(matchedTimeSlotCodes)) {
        return _.compact(_.castArray(submenuOrderPeriodsFallback))
      }
      return matchedTimeSlotCodes
    }

    // from store
    // use store order_periods if available
    const storeCode = _.get(store, 'code')
    const storeOrderingPeriods = _.get(_.find(stores, { code: storeCode }), 'orderingPeriods', [])
    // if (!_.isEmpty(storeOrderingPeriods)) orderingPeriods = storeOrderingPeriods
    const currentSlotCodeFromStore = findMatchedTimeSlotCodes(storeOrderingPeriods)
    if (!_.isEmpty(currentSlotCodeFromStore)) return currentSlotCodeFromStore

    // from system settings
    const vendorKey = getSystemSetting('vendor_specific_system_setting_key')
    const orderingPeriods = getSystemSetting(`vendor_specific_settings.${vendorKey}.ordering_periods`)
    return findMatchedTimeSlotCodes(orderingPeriods)
  }, [store, stores, getSystemSetting])

  const getCurrentSlotCodesAsync = useCallback(async ({ commerceChannelCode, storeId } = {}) => {
    const cc = commerceChannelCode || _.get(orderMethod, 'commerceChannel')
    const commerceChannelConfig = _.get(commerceChannels, `${cc}.publicConfig.${commerceChannelPublicConfigSchemaVersion}`, {})
    const useSubmenuCodeFilter = _.get(commerceChannelConfig, 'useSubmenuCodeFilter', false)

    if (!useSubmenuCodeFilter) return getCurrentSlotCodes()

    const dateTimeInformation = await fetchStoreDateTimeInformation({
      id: storeId || _.get(store, 'id'),
      date: moment().format('YYYY-MM-DD'),
    })
    const submenuOrderPeriods = _.get(dateTimeInformation, 'submenuOrderPeriods', {})
    return getCurrentSlotCodes({ submenuOrderPeriods })
  }, [commerceChannels, orderMethod, store, getCurrentSlotCodes])

  return {
    getCurrentSlotCodes,
    getCurrentSlotCodesAsync,
  }
}

export default useOrderingPeriod
